import type { RefObject } from "react";
import { useEffect, useState } from "react";

import type { Pagination } from "./hook.pagination";
import { sliderItemSelector } from "./item";

/**
 * Enhances the slider to support keyboard events.
 *
 * In particular it will act as a widget such as an input group. This means
 * that once focus is captured then the first focusable item in the slider will
 * be focused and subsequent tab events will bring focus out of the slider to
 * the next applicable item.
 *
 * Once focus is inside the slider then the currently focused target can be
 * changed using keyboard shortcuts.
 */
export function useRegisterKeyboardEvents(
  pagination: Pagination,
  sliderRef: RefObject<HTMLUListElement | null>,
  args: {
    target: "left" | "center" | "right";
  },
): boolean {
  const [hasFocusWithin, setFocusWithin] = useState(false);

  useEffect(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    /**
     * Once the slider becomes focused the first time, then ensure that focus
     * is propagated to the first item within that actually can be focused.
     */
    const onFocusChange = (evt: FocusEvent) => {
      const hasFocusInsideSlider = slider.contains(document.activeElement);
      if (evt.target !== slider) {
        setFocusWithin(hasFocusInsideSlider);
        return;
      }

      const focusedItem = slider.querySelector(
        `${sliderItemSelector.toString()}[data-${
          args.target
        }="true"] a:not([tabindex="-1"]), ${sliderItemSelector.toString()}[data-${
          args.target
        }="true"] button:not([tabindex="-1"]), ${sliderItemSelector.toString()}[data-${
          args.target
        }="true"] *[tabindex]:not([tabindex="-1"])`,
      );

      if (!focusedItem || !(focusedItem instanceof HTMLElement)) {
        return;
      }

      evt.preventDefault();

      focusedItem.focus({ preventScroll: true });
      setFocusWithin(true);
    };

    const onFocusOut = () => {
      setFocusWithin(false);
    };

    const onKeyDown = (evt: KeyboardEvent) => {
      const items = Array.from(slider.children);
      const currentItemIndex = items.findIndex(
        (it) =>
          it === document.activeElement || it.contains(document.activeElement),
      );
      const currentItem = items[Number(currentItemIndex)];

      // Determine the index the the currently focused item has within the
      // actual items (and not the list containing duplicated items, which was
      // the one `currentItemIndex` searches)
      const currentActiveItemIndex = Array.from(
        slider.querySelectorAll(sliderItemSelector.toString()),
      ).findIndex((item) => item === currentItem);

      switch (evt.key) {
        case "ArrowLeft": {
          const nextItem = items[currentItemIndex - 1]?.querySelector(
            "a, button, *[tabindex]",
          );

          if (!(nextItem instanceof HTMLElement)) {
            return;
          }

          nextItem.focus({ preventScroll: true });

          const shouldNavigate =
            pagination.type === "items" || currentActiveItemIndex <= 0;

          if (shouldNavigate) {
            pagination.goBackwards();
          }

          break;
        }

        case "ArrowRight": {
          const nextItem = items[currentItemIndex + 1]?.querySelector(
            "a, button, *[tabindex]",
          );

          if (!(nextItem instanceof HTMLElement)) {
            return;
          }

          nextItem.focus({ preventScroll: true });

          const shouldNavigate =
            pagination.type === "items" ||
            currentActiveItemIndex % pagination.itemsPerPage ===
              pagination.itemsPerPage - 1;

          if (shouldNavigate) {
            pagination.goForwards();
          }

          break;
        }
      }
    };

    window.addEventListener("focus", onFocusChange, { capture: true });
    window.addEventListener("focusout", onFocusOut, { capture: true });
    slider.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("focus", onFocusChange, { capture: true });
      window.removeEventListener("focusout", onFocusOut, { capture: true });
      slider.removeEventListener("keydown", onKeyDown);
    };
  }, [args.target, pagination, sliderRef]);

  return hasFocusWithin;
}
